import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import Tippy from '@tippyjs/react';
import { useRecoilValue } from 'recoil';

import { formatDate } from 'lib/helpers/dateUtils';

import Hr from 'components/shared/Hr/Hr';
import Icon from 'components/shared/Icon/Icon';
import { currentPractice } from 'components/practice/practiceState';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import AcknowledgementList from 'components/shared/AcknowledgementList/AcknowledgementList';
import { isSubstepCompleted } from 'components/shared/OnboardingWizard/lib/resolveContinueAction';

import TrainingsTable from './components/TrainingsTable';
import EditTrainingModal from './components/EditTrainingModal';

const GoLiveDatesAndTrainings = ({
  isAdmin,
  setHeaderButtonProps,
  wizardState: { instance },
  mutateOrchestratorData,
  addFooterActions,
  setFooterConfig,
  orchestratorLoading,
  onNext,
  orchestratorData
}) => {
  const isCompleted = isSubstepCompleted(orchestratorData);
  const [acknowledge, setAcknowledge] = useState(false);
  const { practiceId } = useOutletContext();
  const practice = useRecoilValue(currentPractice);
  const timezone = practice?.timezone;

  const [selectedDates, setSelectedDates] = useState({
    softDate: instance?.soft_go_live_date,
    hardDate: instance?.hard_go_live_date
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);

  const handleSoftDateChange = (date) => {
    setSelectedDates((prev) => ({
      ...prev,
      softDate: date
    }));
  };

  const handleHardDateChange = (date) => {
    setSelectedDates((prev) => ({
      ...prev,
      hardDate: date
    }));
  };

  const handleEditTraining = (training) => {
    setEditingTraining(training);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingTraining(null);
  };

  // Normalize dates for comparison - convert both to the same format
  const normalizeDate = (date) => {
    if (!date) return null;
    // If it's already in MM/DD/YYYY format, return as is
    if (typeof date === 'string' && date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return date;
    }
    // If it's an ISO string or Date object, convert to MM/DD/YYYY
    return formatDate(date, null, 'MM/dd/yyyy');
  };

  const dirtyForm =
    normalizeDate(selectedDates.softDate) !== normalizeDate(instance?.soft_go_live_date) ||
    normalizeDate(selectedDates.hardDate) !== normalizeDate(instance?.hard_go_live_date);

  const hasSelectedDates = selectedDates.softDate && selectedDates.hardDate;

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      {
        text: 'Continue',
        onClick:
          dirtyForm || (!isCompleted && isAdmin && hasSelectedDates)
            ? () => mutateOrchestratorData(selectedDates)
            : onNext,
        isDisabled: orchestratorLoading || (!isAdmin && !acknowledge)
      }
    ]);
    setFooterConfig((prev) => ({
      ...prev,
      aboveFooterContent: (
        <AcknowledgementList
          header={{
            title: 'Training attendance requirement',
            description:
              'Timely attendance and completion of any required preparation are essential to reaching your go-live goals. Preparation items will be provided with due dates in advance so sessions can be efficient and productive.'
          }}
          items={[]}
          acknowledgement={{
            text: 'I acknowledge the training requirements and confirm that our team will attend all scheduled sessions.',
            isChecked: isCompleted || acknowledge,
            onChange: () => setAcknowledge(!acknowledge),
            isDisabled: isCompleted
          }}
        />
      )
    }));

    return () => {
      setFooterConfig((prev) => ({ ...prev, aboveFooterContent: null }));
    };
  }, [
    orchestratorLoading,
    selectedDates.softDate,
    selectedDates.hardDate,
    dirtyForm,
    acknowledge,
    isCompleted,
    isAdmin
  ]);

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
        <div id="soft-go-live-date" className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-500">Original estimated go-live date</label>
            <Tippy
              content="Initial target from paperwork, for timeline context."
              placement="top"
              className="tippy-dark">
              <div className="flex h-6 w-6 cursor-help items-center justify-center">
                <Icon size={16} icon="info-circle-mono" className="text-neutral-500" />
              </div>
            </Tippy>
          </div>
          <DateInputPicker
            id="soft-go-live-date"
            minDate={new Date()}
            onChange={handleSoftDateChange}
            value={selectedDates.softDate}
            isClearable={false}
            disabled={!isAdmin}
          />
        </div>
        <div id="hard-go-live-date" className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-500">Hard launch date</label>
            <Tippy
              content="Goal for official launch with real patients; must complete all onboarding assignments before this date. This will be a confirmed appointment with a dedicated technician on-call to ensure a smooth first day."
              placement="top"
              className="tippy-dark">
              <div className="flex h-6 w-6 cursor-help items-center justify-center">
                <Icon size={16} icon="info-circle-mono" className="text-neutral-500" />
              </div>
            </Tippy>
          </div>
          <DateInputPicker
            id="hard-go-live-date"
            minDate={new Date()}
            onChange={handleHardDateChange}
            value={selectedDates.hardDate}
            isClearable={false}
          />
        </div>
      </div>

      <Hr className="my-2" />

      <div className="flex flex-col gap-4">
        <h3 className="text-base font-medium">Trainings</h3>
        <TrainingsTable
          practiceId={practiceId}
          onEditTraining={handleEditTraining}
          isAdmin={isAdmin}
          timezone={timezone}
        />
      </div>

      <EditTrainingModal
        isOpen={editModalOpen}
        handleClose={handleCloseEditModal}
        training={editingTraining}
        practiceId={practiceId}
        hardGoLiveDate={selectedDates.hardDate}
        timezone={timezone}
      />
    </div>
  );
};

export default GoLiveDatesAndTrainings;
